import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import { of, startWith, Subscription, switchMap } from 'rxjs';
import { CommonHelper, ProfileEntity } from '@meplis/common';
import { ProfileDisplayNamePipe } from '@cnp/pipes';
import { Router } from '@angular/router';
import { environment } from '@meplis/environments';
import { AppRoutes } from '@cnp/common';
import { AuthenticationService } from '../../../../services/application/authentication.service';
import { i18nService, ToastService } from '@meplis/services';
import { MatMenuModule } from '@angular/material/menu';
import { ProfileAvatarComponent } from '../profile-avatar/profile-avatar.component';
import { ProfileService } from '@cnp/services';
import { APP_VERSION, APP_BUILD_DATE } from '../../../../../version';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { ProfileCompletenessComponent } from '../profile-completeness/profile-completeness.component';

@Component({
  selector: 'app-profile-widget',
  imports: [
    TranslatePipe,
    MatMenuModule,
    ProfileAvatarComponent,
    ProfileDisplayNamePipe,
    FaIconComponent,
    ProfileCompletenessComponent
],
  templateUrl: './profile-widget.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./profile-widget.component.scss'],
})
export class ProfileWidgetComponent implements OnInit, OnDestroy {
  _router = inject(Router);
  private _authService = inject(AuthenticationService);
  private _i18nService = inject(i18nService);
  private _profileService = inject(ProfileService);
  private _toastService = inject(ToastService);

  public accountUrl: string;
  public imageAvatarUrl: string;
  public profile: ProfileEntity;

  public subscription: Subscription;
  public profileChangedSubscription: Subscription;
  public appRoutes = AppRoutes;
  public readonly appVersion = APP_VERSION;
  public readonly appBuildDate = APP_BUILD_DATE;

  constructor() {
    this.subscription = this._authService.onLoggedInOut
      .pipe(
        startWith(null),
        switchMap(() => {
          if (!this._authService.isLogged) {
            return of(null as typeof this.profile);
          }
          return this._profileService.getProfile();
        })
      )
      .subscribe(async (profile) => {
        this.profile = profile;
        this._handleInitialization();
      });

    this.profileChangedSubscription = this._profileService.onProfileChanged
      .pipe(switchMap(() => this._profileService.getProfile()))
      .subscribe((profile) => {
        this.profile = profile;
        this._handleInitialization();
      });
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.profileChangedSubscription?.unsubscribe();
  }

  public async ngOnInit(): Promise<void> {
    this._handleInitialization();
  }

  public async _handleInitialization(): Promise<void> {
    if (!this._authService.isLogged) {
      return;
    }

    this.imageAvatarUrl = CommonHelper.getAvatarImage(this.profile?.id);

    const socialUrl = environment.social;
    const language = this._i18nService.language;
    const profileId = this.profile?.id;

    this.accountUrl = `${socialUrl}/${language}/profiles/${profileId}`;
  }

  public goToPage(path: string): void {
    this._router.navigate([path]);
  }

  public logout(): void {
    // Land on the public home page after signing out (root route `/`).
    const logoutResult = this._authService.logOut(AppRoutes['HOME'].fullPath);
    if (logoutResult) {
      this._toastService.showSuccess('AUTHENTICATION.LOGOUT_SUCCESS_MESSAGE');
    }
  }
}
